import { Modal } from '@/components/admin/Modal';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  AGENT_LOG_STATUS_META,
  type AgentLogStatus,
  SEVERITY_BAR_CLASS,
  SEVERITY_CLASSES,
  SEVERITY_TEXT_CLASS,
  deriveEntryStatus,
  deriveOverallStatus,
} from '@/lib/insight-agent-log-status';
import type { InsightAgentLogEntry, ToolCallExternalRef } from '@innermate/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Check, ChevronLeft, ChevronRight, Copy, TriangleAlert } from 'lucide-react';
import {
  type ReactNode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const MonacoEditor = lazy(() =>
  import('@monaco-editor/react').then((m) => ({ default: m.default })),
);

interface Props {
  open: boolean;
  onClose: () => void;
  externalRef: ToolCallExternalRef;
  turnIndex: number;
  /** 이 턴의 사용자 질문(role='user' 메시지 content). null 이면 description 미노출. */
  userQuestion?: string | null;
  /** 이전/다음 로그-턴이 있을 때 호출. 없으면 해당 방향 버튼/키 disabled. */
  onPrev?: () => void;
  onNext?: () => void;
  /** "current / total" 표시 (1-based). 둘 다 있어야 nav UI 노출. */
  position?: { current: number; total: number };
}

/**
 * Insight Agent 외부 로그를 조회/표시하는 master/detail 모달.
 *
 * 레이아웃:
 *  - 헤더: overall status 배지 + 설명 + 통계 + inferred 안내
 *  - 좌 25%: LLM 호출 목록 표 (각 행에 타임라인 막대 inline, 행 선택 + 키보드 ↑↓)
 *  - 우 75%: 좌 Prompt | 우 (Input 위 / Output 아래) — 1:1 가로 + 1:1 세로 (monaco) + Error 박스
 */
export function InsightAgentLogModal({
  open,
  onClose,
  externalRef,
  turnIndex,
  userQuestion,
  onPrev,
  onNext,
  position,
}: Props) {
  const q = useQuery({
    queryKey: ['admin', 'insight-agent-log', externalRef.sessionId, externalRef.rowId],
    queryFn: () => api.adminGetInsightAgentLog(externalRef.sessionId, externalRef.rowId),
    enabled: open,
    staleTime: 5 * 60_000, // 5분 캐시
    // 턴 네비게이션 시 queryKey 변경되어도 이전 데이터를 보여주며 로딩 — 화면 깜빡임 방지
    placeholderData: keepPreviousData,
  });

  // 멀티라인 질문은 공백으로 정규화 (description 슬롯이 prose 한 줄/짧은 wrap 가정).
  // 표시는 "Q. ..." 접두어, clipboard 복사는 raw 질문만 (다른 도구에 paste 시 깔끔).
  const normalizedQuestion = userQuestion?.replace(/\s+/g, ' ').trim() || undefined;
  const description = normalizedQuestion ? `Q. ${normalizedQuestion}` : undefined;

  // Shift+← / Shift+→ — 모달 열려있고 키 input 안이 아닐 때만.
  const canPrev = position != null && position.current > 1 && onPrev != null;
  const canNext = position != null && position.current < position.total && onNext != null;
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (!e.shiftKey) return;
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      }
      if (e.key === 'ArrowLeft' && canPrev) {
        e.preventDefault();
        onPrev?.();
      } else if (e.key === 'ArrowRight' && canNext) {
        e.preventDefault();
        onNext?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, canPrev, canNext, onPrev, onNext]);

  const titleActions =
    position != null ? (
      <div className="flex items-center gap-1 text-xs tabular-nums">
        <button
          type="button"
          onClick={() => canPrev && onPrev?.()}
          disabled={!canPrev}
          aria-label="이전 로그-턴"
          title="이전 로그-턴 (Shift+←)"
          className={cn(
            'p-1 rounded border border-border',
            canPrev
              ? 'text-foreground hover:bg-surface-muted cursor-pointer'
              : 'text-foreground-muted opacity-40 cursor-not-allowed',
          )}
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="px-2 text-foreground-muted">
          {position.current} / {position.total}
        </span>
        <button
          type="button"
          onClick={() => canNext && onNext?.()}
          disabled={!canNext}
          aria-label="다음 로그-턴"
          title="다음 로그-턴 (Shift+→)"
          className={cn(
            'p-1 rounded border border-border',
            canNext
              ? 'text-foreground hover:bg-surface-muted cursor-pointer'
              : 'text-foreground-muted opacity-40 cursor-not-allowed',
          )}
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    ) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`에이전트 로그 — Turn ${turnIndex + 1} (Row ID ${externalRef.rowId})`}
      description={description}
      descriptionCopy={normalizedQuestion}
      titleActions={titleActions}
      size="full"
    >
      <div className="flex flex-col gap-3 h-full">
        {q.isLoading && (
          <div className="grid place-items-center py-12 text-sm text-foreground-muted">
            에이전트 로그 가져오는 중…
          </div>
        )}
        {q.isError && <LogError error={q.error} externalRef={externalRef} />}
        {q.data && q.data.length === 0 && (
          <div className="grid place-items-center py-12 text-sm text-foreground-muted">
            이 row_id 에 대한 로그가 없습니다.
            {externalRef.inferred && ' (추정된 row_id 가 어긋났을 수 있습니다)'}
          </div>
        )}
        {q.data && q.data.length > 0 && <LogContent entries={q.data} externalRef={externalRef} />}
      </div>
    </Modal>
  );
}

// ─── 본문 (loaded 상태) ────────────────────────────────────────────────────────

function LogContent({
  entries,
  externalRef,
}: {
  entries: InsightAgentLogEntry[];
  externalRef: ToolCallExternalRef;
}) {
  // 시작 시간 순 정렬 (병렬 호출은 start 순서 — 사용자 시각화 의도).
  const sorted = useMemo(() => {
    return [...entries].sort((a, b) => {
      if (a.startTimestamp < b.startTimestamp) return -1;
      if (a.startTimestamp > b.startTimestamp) return 1;
      return 0;
    });
  }, [entries]);

  // 첫 에러 entry 가 있으면 우선 선택, 없으면 0번. 방어 — 빈 배열이면 -1 (호출자에서 가드)
  const initialIdx = useMemo(() => {
    const errIdx = sorted.findIndex((e) => e.errorType !== null);
    return errIdx >= 0 ? errIdx : sorted.length > 0 ? 0 : -1;
  }, [sorted]);
  const [selectedIdx, setSelectedIdx] = useState(initialIdx);
  // entries 가 바뀌면 selection 리셋
  useEffect(() => {
    setSelectedIdx(initialIdx);
  }, [initialIdx]);

  // 키보드 ↑↓ navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        // input/textarea 안에서는 navigation 비활성
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((i) => Math.min(sorted.length - 1, i + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sorted.length]);

  const selected = selectedIdx >= 0 ? sorted[selectedIdx] : undefined;

  return (
    <>
      <Header entries={sorted} externalRef={externalRef} />
      <div className="grid grid-cols-[1fr_3fr] gap-4 flex-1 min-h-0">
        <EntryTable entries={sorted} selectedIdx={selectedIdx} onSelect={setSelectedIdx} />
        <div className="min-h-0 flex flex-col">
          {selected ? (
            <DetailPanel entry={selected} />
          ) : (
            <div className="grid place-items-center h-full text-sm text-foreground-muted">
              호출을 선택하세요.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── 헤더 (3×3 필드 그리드) ────────────────────────────────────────────────────
//
// 행1: 세션 ID | Row ID | 상태
// 행2: 최초 시작 | 최종 종료 | 소요 시간
// 행3: 총 호출 | 입력 토큰 | 출력 토큰

function Header({
  entries,
  externalRef,
}: {
  entries: InsightAgentLogEntry[];
  externalRef: ToolCallExternalRef;
}) {
  const overall = useMemo(() => deriveOverallStatus(entries), [entries]);
  const totals = useMemo(() => {
    let inputs = 0;
    let outputs = 0;
    let minStart: string | null = null;
    let maxEnd: string | null = null;
    for (const e of entries) {
      inputs += e.inputToken;
      outputs += e.outputToken;
      if (minStart === null || e.startTimestamp < minStart) minStart = e.startTimestamp;
      if (maxEnd === null || e.endTimestamp > maxEnd) maxEnd = e.endTimestamp;
    }
    const dur =
      minStart && maxEnd
        ? Math.max(0, new Date(maxEnd).getTime() - new Date(minStart).getTime())
        : 0;
    return { inputs, outputs, minStart, maxEnd, durationMs: dur };
  }, [entries]);

  const fields: { label: string; node: ReactNode }[] = [
    { label: '세션 ID', node: <SessionIdValue sessionId={externalRef.sessionId} /> },
    {
      label: 'Row ID',
      node: <RowIdValue rowId={externalRef.rowId} inferred={externalRef.inferred} />,
    },
    {
      label: '상태',
      node: overall ? <StatusChip status={overall} /> : <Muted>-</Muted>,
    },
    { label: '최초 시작', node: <TimestampValue iso={totals.minStart} /> },
    { label: '최종 종료', node: <TimestampValue iso={totals.maxEnd} /> },
    { label: '소요 시간', node: <span className="tabular-nums">{fmtMs(totals.durationMs)}</span> },
    {
      label: '입력 토큰',
      node: <span className="tabular-nums">{totals.inputs.toLocaleString()}</span>,
    },
    {
      label: '출력 토큰',
      node: <span className="tabular-nums">{totals.outputs.toLocaleString()}</span>,
    },
    {
      label: '토큰 합계',
      node: (
        <span className="tabular-nums">{(totals.inputs + totals.outputs).toLocaleString()}</span>
      ),
    },
  ];

  return (
    <div className="border border-border rounded-md bg-surface-muted shrink-0 p-3">
      <div className="grid grid-cols-3 grid-rows-3 grid-flow-col gap-x-8 gap-y-1.5">
        {fields.map((f) => (
          <div key={f.label} className="flex items-center gap-2 min-w-0 text-xs">
            <span className="w-20 shrink-0 text-foreground-muted">{f.label}</span>
            <div className="min-w-0 flex-1">{f.node}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: AgentLogStatus }) {
  const meta = AGENT_LOG_STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px]',
        SEVERITY_CLASSES[meta.severity],
      )}
    >
      <Icon className="size-3" />
      {status}
    </span>
  );
}

function SessionIdValue({ sessionId }: { sessionId: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(sessionId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }, [sessionId]);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-mono truncate min-w-0" title={sessionId}>
        {sessionId}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 text-foreground-muted hover:text-foreground cursor-pointer"
        aria-label="세션 ID 복사"
        title={copied ? '복사됨' : '복사'}
      >
        {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      </button>
    </div>
  );
}

function RowIdValue({ rowId, inferred }: { rowId: number; inferred?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="tabular-nums">{rowId}</span>
      {inferred ? (
        <span
          className="inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded border border-amber-400/30 bg-amber-500/5 text-amber-600"
          title="verify_tag API 실패로 추정된 row_id 입니다. 로그가 비어있다면 추정이 어긋났을 수 있습니다."
        >
          <TriangleAlert className="size-2.5" />
          추정
        </span>
      ) : null}
    </div>
  );
}

function TimestampValue({ iso }: { iso: string | null }) {
  if (!iso) return <Muted>-</Muted>;
  const d = new Date(iso);
  // "2026-05-12 08:29:48" (ms 제거)
  const formatted = d.toISOString().replace('T', ' ').slice(0, 19);
  return <span className="font-mono tabular-nums">{formatted}</span>;
}

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-foreground-muted">{children}</span>;
}

// ─── 표 (타임라인 inline) ────────────────────────────────────────────────────

function EntryTable({
  entries,
  selectedIdx,
  onSelect,
}: {
  entries: InsightAgentLogEntry[];
  selectedIdx: number;
  onSelect: (i: number) => void;
}) {
  const range = useMemo(() => {
    if (entries.length === 0) return { start: 0, end: 1 };
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const e of entries) {
      const s = new Date(e.startTimestamp).getTime();
      const en = new Date(e.endTimestamp).getTime();
      if (s < min) min = s;
      if (en > max) max = en;
    }
    return { start: min, end: max };
  }, [entries]);
  const span = Math.max(1, range.end - range.start);

  // 선택된 행을 viewport 안으로 스크롤
  const rowRefs = useRef<Map<number, HTMLTableRowElement | null>>(new Map());
  useEffect(() => {
    const el = rowRefs.current.get(selectedIdx);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedIdx]);

  return (
    <div className="border border-border rounded-md overflow-auto flex-1 min-h-0">
      <table className="w-full text-[11px] tabular-nums">
        <thead className="sticky top-0 bg-surface-muted border-b border-border">
          <tr className="text-left">
            <th className="px-2 py-1.5 w-8">#</th>
            <th className="px-2 py-1.5 w-32">호출 / 상태</th>
            <th className="px-2 py-1.5">타임라인</th>
            <th className="px-2 py-1.5 w-14 text-right">소요</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => {
            const start = new Date(e.startTimestamp).getTime();
            const end = new Date(e.endTimestamp).getTime();
            const leftPct = ((start - range.start) / span) * 100;
            const widthPct = Math.max(0.3, ((end - start) / span) * 100);
            const status = deriveEntryStatus(e);
            const meta = AGENT_LOG_STATUS_META[status];
            const Icon = meta.icon;
            const isSelected = i === selectedIdx;
            const isError = meta.severity === 'error';
            return (
              <tr
                key={`${e.startTimestamp}-${e.llmName}-${i}`}
                ref={(el) => {
                  rowRefs.current.set(i, el);
                }}
                onClick={() => onSelect(i)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    onSelect(i);
                  }
                }}
                tabIndex={isSelected ? 0 : -1}
                aria-selected={isSelected}
                className={cn(
                  'cursor-pointer border-b border-border/50 hover:bg-surface-muted/60',
                  isSelected && 'bg-accent/10',
                  isError && !isSelected && 'bg-red-500/5',
                )}
                title={`${e.llmName} — ${status}`}
              >
                <td className="px-2 py-1 text-foreground-muted align-middle">{i + 1}</td>
                <td className="px-2 py-1 align-middle">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 max-w-full',
                      SEVERITY_TEXT_CLASS[meta.severity],
                    )}
                  >
                    <Icon className="size-3 shrink-0" />
                    <span className="truncate text-foreground" title={e.llmName}>
                      {e.llmName}
                    </span>
                  </span>
                </td>
                <td className="px-2 py-1 align-middle">
                  <div className="relative h-2.5 bg-foreground/10 rounded-sm min-w-20">
                    <div
                      className={cn(
                        'absolute h-full rounded-sm',
                        SEVERITY_BAR_CLASS[meta.severity],
                      )}
                      style={{ left: `${leftPct}%`, width: `${widthPct}%`, minWidth: '2px' }}
                    />
                  </div>
                </td>
                <td className="px-2 py-1 text-right align-middle">{fmtMsShort(end - start)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── 우측 — 헤더(메타 + status + errorMsg) + Input/Prompt/Output ─────────────

function DetailPanel({ entry }: { entry: InsightAgentLogEntry }) {
  const status = deriveEntryStatus(entry);
  const meta = AGENT_LOG_STATUS_META[status];
  const Icon = meta.icon;
  const start = new Date(entry.startTimestamp);
  const end = new Date(entry.endTimestamp);
  const durMs = Math.max(0, end.getTime() - start.getTime());

  const fmtTs = (d: Date) => d.toISOString().replace('T', ' ').slice(0, 19);

  return (
    <div className="flex flex-col gap-3 pr-1 h-full min-h-0">
      <div className="border border-border rounded-md p-3 bg-surface-muted shrink-0 flex flex-col gap-2">
        {/* Identity — icon + llmName | (여백) | status chip */}
        <div className="flex items-center gap-2">
          <Icon className={cn('size-4 shrink-0', SEVERITY_TEXT_CLASS[meta.severity])} />
          <span className="font-medium text-sm truncate min-w-0" title={entry.llmName}>
            {entry.llmName}
          </span>
          <span
            className={cn(
              'text-[11px] px-1.5 py-0.5 rounded border shrink-0 ml-auto',
              SEVERITY_CLASSES[meta.severity],
            )}
          >
            {status}
          </span>
        </div>
        {/* Metrics — metrics 6종 (좁아지면 내부 wrap) | modelName chip(우측 고정, wrap 안 됨) */}
        <div className="flex items-baseline gap-x-6 text-xs">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 min-w-0 flex-1">
            <MetricPair label="시작" value={fmtTs(start)} mono />
            <MetricPair label="종료" value={fmtTs(end)} mono />
            <MetricPair label="소요" value={fmtMs(durMs)} mono />
            <MetricPair label="입력" value={entry.inputToken.toLocaleString()} mono />
            <MetricPair label="출력" value={entry.outputToken.toLocaleString()} mono />
            <MetricPair
              label="합계"
              value={(entry.inputToken + entry.outputToken).toLocaleString()}
              mono
            />
          </div>
          <span
            className="shrink-0 inline-flex items-center text-[11px] px-1.5 py-0.5 rounded border border-border bg-surface text-foreground-muted font-mono truncate max-w-72"
            title={entry.modelName}
          >
            {entry.modelName}
          </span>
        </div>
        {entry.errorMsg ? (
          <pre className="text-[11px] font-mono whitespace-pre-wrap break-all max-h-24 overflow-y-auto bg-surface rounded p-2 border border-red-400/30 text-red-600">
            {entry.errorMsg}
          </pre>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 flex-1 min-h-0">
        <CodeSection label="Prompt" content={entry.prompt} />
        <div className="grid grid-rows-2 gap-3 min-h-0">
          <CodeSection label="Input" content={entry.inputValue} />
          <CodeSection label="Output" content={entry.answer} />
        </div>
      </div>
    </div>
  );
}

function MetricPair({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-foreground-muted">{label}</span>
      <span className={mono ? 'font-mono tabular-nums' : 'tabular-nums'}>{value}</span>
    </div>
  );
}

function CodeSection({
  label,
  content,
}: {
  label: string;
  content: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const language = useMemo(() => detectLanguage(content), [content]);
  const text = content ?? '';

  const handleCopy = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }, [text]);

  return (
    <div className="border border-border rounded-md overflow-hidden flex flex-col min-h-0">
      <div className="flex items-center justify-between px-3 py-1.5 bg-surface-muted border-b border-border shrink-0">
        <span className="text-xs font-medium">{label}</span>
        {content !== null ? (
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1 text-[11px] text-foreground-muted hover:text-foreground cursor-pointer"
          >
            {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            {copied ? '복사됨' : '복사'}
          </button>
        ) : null}
      </div>
      {content === null ? (
        <div className="p-3 text-xs text-foreground-muted italic flex-1">없음</div>
      ) : (
        <div className="flex-1 min-h-0">
          <Suspense
            fallback={<div className="p-3 text-xs text-foreground-muted">에디터 로딩 중…</div>}
          >
            <MonacoEditor
              height="100%"
              language={language}
              value={text}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: false },
                fontSize: 12,
                lineNumbers: 'on',
                wordWrap: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
}

function detectLanguage(content: string | null): string {
  if (!content) return 'plaintext';
  const trimmed = content.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // fall through
    }
  }
  return 'plaintext';
}

// ─── 에러 ────────────────────────────────────────────────────────────────────

function LogError({ error, externalRef }: { error: unknown; externalRef: ToolCallExternalRef }) {
  const status = (error as { status?: number } | null)?.status ?? 0;
  let title = '에이전트 로그 조회 실패';
  let body = '';
  if (status === 404) {
    title = externalRef.inferred ? '추정된 row_id 가 어긋났습니다' : '등록되지 않은 row_id 입니다';
    body = externalRef.inferred
      ? 'verify_tag 실패로 추정된 row_id 가 실제 에이전트의 row_id 와 다를 수 있습니다. 에이전트 측 확인이 필요합니다.'
      : '이 sessionId/rowId 조합이 데이터베이스에 등록돼 있지 않습니다.';
  } else if (status === 503) {
    title = '에이전트 로그 서버 응답 실패';
    body = '잠시 후 다시 시도해 주세요.';
  } else {
    body = error instanceof Error ? error.message : '알 수 없는 오류';
  }
  return (
    <div className="grid place-items-center py-12 text-sm">
      <div className="text-center">
        <p className="font-medium">{title}</p>
        {body ? <p className="text-foreground-muted mt-1 text-xs">{body}</p> : null}
      </div>
    </div>
  );
}

// ─── 포맷 헬퍼 ───────────────────────────────────────────────────────────────

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function fmtMsShort(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}
